import { NumberInput, Stack, Text, TextInput } from '@mantine/core';
import { useRef, useState } from 'react';
import StyledInputErrorText from '@/components/StyledInputFields/StyledInputErrorText.tsx';
import type { IntervalSchedule } from '@/types/automationSteps.ts';
import { createDefaultSchedule, type ScheduleDraft, validateScheduleDraft } from './scheduledTriggerSchedule.ts';

type Props = {
  schedule?: IntervalSchedule | null;
  onSave: (schedule: IntervalSchedule) => void;
  serverError?: string;
};

const scheduleKey = (schedule: IntervalSchedule) =>
  `${schedule.every_minutes}:${new Date(schedule.start_at).getTime()}`;

export default function ScheduledTriggerFields({ schedule, onSave, serverError }: Props) {
  const [draft, setDraft] = useState<ScheduleDraft>(() => schedule ?? createDefaultSchedule());
  const [showErrors, setShowErrors] = useState(false);
  const lastSubmitted = useRef(schedule ? scheduleKey(schedule) : null);
  const validation = validateScheduleDraft(draft);

  const save = () => {
    setShowErrors(true);
    const validSchedule = validation.schedule;
    if (validSchedule && scheduleKey(validSchedule) !== lastSubmitted.current) {
      lastSubmitted.current = scheduleKey(validSchedule);
      onSave(validSchedule);
    }
  };

  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        Runs on a UTC anchored interval while this trigger is published. The first run
        occurs at the next interval boundary after publication.
      </Text>
      <NumberInput
        label="Every (minutes)"
        description="Changes save when you leave a field or press Enter."
        min={5}
        max={525600}
        step={5}
        clampBehavior="none"
        value={draft.every_minutes}
        error={showErrors ? validation.intervalError : undefined}
        onChange={(value) => {
          lastSubmitted.current = null;
          setDraft((current) => ({ ...current, every_minutes: value }));
        }}
        onBlur={save}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
      />
      <TextInput
        label="Start at (UTC)"
        description="ISO 8601 UTC time, such as 2026-11-01T12:00:00Z"
        value={draft.start_at}
        error={showErrors ? validation.startError : undefined}
        onChange={(event) => {
          const value = event.currentTarget.value;
          lastSubmitted.current = null;
          setDraft((current) => ({ ...current, start_at: value }));
        }}
        onBlur={save}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
      />
      {showErrors && !validation.schedule && (
        <Text size="xs" c="red" role="status">These schedule changes have not been saved.</Text>
      )}
      {serverError && <StyledInputErrorText>{serverError}</StyledInputErrorText>}
    </Stack>
  );
}
